import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import type {
  Cliente,
  EstadoExpediente,
  EstadoProcesal,
  Expediente,
  Fuero,
  Materia,
  NivelRiesgo,
  Prioridad,
  ProbabilidadExito,
  Submateria,
  ValorEstrategico,
} from "../../../shared/types";
import { useAuth } from "../../auth/AuthContext";
import { Boton } from "../../components/Boton";
import { CampoBusqueda } from "../../components/Formulario";
import { EncabezadoPagina } from "../../components/EncabezadoPagina";
import { Icono } from "../../components/Icono";
import { Insignia } from "../../components/Insignia";
import { PanelLateral } from "../../components/PanelLateral";
import { Tabla, type ColumnaTabla } from "../../components/Tabla";
import { Tarjeta } from "../../components/Tarjeta";
import { Select } from "../../components/Formulario";
import { useEstadoEnUrl } from "../../hooks/useEstadoEnUrl";
import { api, ErrorApi } from "../../lib/apiClient";
import { COLOR_POR_PROBABILIDAD, COLOR_POR_RIESGO, resolverColorSemantico } from "../../lib/colorSemantico";
import { useToast } from "../../components/Toast";
import { FormularioExpediente } from "./FormularioExpediente";

type ExpedienteConCliente = Expediente & {
  cliente_nombre: string;
  ultima_actuacion: string | null;
};

// Semáforo de inactividad: mismo umbral de "sin movimiento" ya usado en el
// Dashboard/Centro de Operaciones (30 días) más una zona de alerta
// intermedia a los 15 — sin inventar un nuevo criterio de "sin movimiento",
// sólo una escala visual sobre el mismo dato.
function diasSinMovimiento(e: ExpedienteConCliente): number {
  const referencia = e.ultima_actuacion ?? e.creado_en;
  return Math.max(Math.round((Date.now() - new Date(referencia).getTime()) / 86400000), 0);
}

function colorSemaforo(dias: number): string {
  if (dias > 30) return "bg-error";
  if (dias >= 15) return "bg-advertencia";
  return "bg-exito";
}

// Barra de avance compartida entre la vista Tabla y la vista Tarjetas —
// mismo dato (avance_manual_pct), misma representación visual, sólo varía
// el ancho disponible en cada contenedor.
function BarraAvance({ pct }: { pct: number }) {
  return (
    <div className="w-24">
      <div className="mb-1 flex items-center justify-between text-xs text-texto-secundario">
        <span>Avance</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-borde">
        <div
          className="h-full rounded-full bg-secundario"
          style={{ width: `${Math.max(pct, 3)}%` }}
        />
      </div>
    </div>
  );
}

const ESTADOS: EstadoExpediente[] = [
  "Consulta Inicial",
  "En Trámite",
  "Con Resolución",
  "En Ejecución o Cobro",
  "Archivado",
  "Desistido",
];
const PRIORIDADES: Prioridad[] = ["Alta", "Media", "Baja"];
const NIVELES_RIESGO: NivelRiesgo[] = ["Bajo", "Medio", "Alto", "Crítico"];
const PROBABILIDADES: ProbabilidadExito[] = [
  "No evaluada",
  "Muy baja",
  "Baja",
  "Media",
  "Alta",
  "Muy alta",
];

const CLAVES_FILTROS_URL = [
  "estado",
  "prioridad",
  "materia_id",
  "submateria_id",
  "fuero_id",
  "estado_procesal_id",
  "nivel_riesgo",
  "probabilidad_exito",
  "valor_estrategico_id",
  "solo_estrategicos",
];

export function ExpedientesListaPage() {
  const navegar = useNavigate();
  const location = useLocation();
  // Sólo para "Limpiar filtros": useEstadoEnUrl gestiona cada clave de forma
  // independiente (una llamada a setSearchParams por clave), correcto para
  // limpiar un filtro a la vez (chips), pero incorrecto para limpiar varios
  // de una sola vez — cada llamada synchrónica parte del mismo searchParams
  // "stale" previo al click, así que sólo la última sobrevive. Acá se arma
  // un único setSearchParams que borra todas las claves de una vez.
  const [, setSearchParams] = useSearchParams();
  const { mostrar } = useToast();
  const { permisos, sinAreasAsignadas } = useAuth();
  const veEvaluacion = permisos.has("expedientes.ver_evaluacion");
  const [expedientes, setExpedientes] = useState<ExpedienteConCliente[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [cargando, setCargando] = useState(true);
  // Todos los filtros del listado viven en la URL (useEstadoEnUrl) para que
  // "volver" desde la ficha de un expediente conserve exactamente el mismo
  // contexto de búsqueda — antes sólo búsqueda/estado/prioridad/vista lo
  // hacían, los filtros de evaluación quedaban como estado local y se
  // perdían al volver (ver ROADMAP.md).
  const [busqueda, setBusqueda] = useEstadoEnUrl("busqueda", "");
  const [estado, setEstado] = useEstadoEnUrl("estado", "");
  const [prioridad, setPrioridad] = useEstadoEnUrl("prioridad", "");
  const [materiaId] = useEstadoEnUrl("materia_id", "");
  const [submateriaId, setSubmateriaId] = useEstadoEnUrl("submateria_id", "");
  const [fueroId, setFueroId] = useEstadoEnUrl("fuero_id", "");
  const [estadoProcesalId, setEstadoProcesalId] = useEstadoEnUrl("estado_procesal_id", "");
  const [nivelRiesgo, setNivelRiesgo] = useEstadoEnUrl("nivel_riesgo", "");
  const [probabilidadExito, setProbabilidadExito] = useEstadoEnUrl("probabilidad_exito", "");
  const [valorEstrategicoId, setValorEstrategicoId] = useEstadoEnUrl("valor_estrategico_id", "");
  const [soloEstrategicos, setSoloEstrategicos] = useEstadoEnUrl("solo_estrategicos", "");
  const [materias, setMaterias] = useState<Materia[]>([]);
  const [submaterias, setSubmaterias] = useState<Submateria[]>([]);
  const [fueros, setFueros] = useState<Fuero[]>([]);
  const [estadosProcesales, setEstadosProcesales] = useState<EstadoProcesal[]>([]);
  const [valoresEstrategicos, setValoresEstrategicos] = useState<ValorEstrategico[]>([]);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [vista, setVista] = useEstadoEnUrl("vista", "tabla");
  const [filtrosAbiertosUrl, setFiltrosAbiertosUrl] = useEstadoEnUrl("filtros", "");
  const filtrosAbiertos = filtrosAbiertosUrl === "1";
  function setFiltrosAbiertos(abierto: boolean) {
    setFiltrosAbiertosUrl(abierto ? "1" : "");
  }

  // Ruta exacta (con filtros) a la que debe volver la ficha de un
  // expediente cuando el usuario llegó desde acá.
  function irAFicha(id: string) {
    navegar(`/expedientes/${id}`, { state: { from: location.pathname + location.search } });
  }

  // Cambia dos o más claves de filtro de una sola vez (ej. limpiar
  // Submateria al cambiar Materia) — ver el comentario de setSearchParams
  // más arriba: llamar a dos setters de useEstadoEnUrl por separado en el
  // mismo handler no compone, cada uno parte del mismo searchParams previo
  // al click y sólo el último sobrevive.
  function actualizarFiltrosUrl(cambios: Record<string, string>) {
    setSearchParams(
      (previos) => {
        const siguientes = new URLSearchParams(previos);
        for (const [clave, valor] of Object.entries(cambios)) {
          if (valor) siguientes.set(clave, valor);
          else siguientes.delete(clave);
        }
        return siguientes;
      },
      { replace: true },
    );
  }

  const parametros = useMemo(() => {
    const p = new URLSearchParams();
    if (busqueda) p.set("busqueda", busqueda);
    if (estado) p.set("estado", estado);
    if (prioridad) p.set("prioridad", prioridad);
    if (materiaId) p.set("materia_id", materiaId);
    if (submateriaId) p.set("submateria_id", submateriaId);
    if (fueroId) p.set("fuero_id", fueroId);
    if (veEvaluacion && estadoProcesalId) p.set("estado_procesal_id", estadoProcesalId);
    if (veEvaluacion && nivelRiesgo) p.set("nivel_riesgo", nivelRiesgo);
    if (veEvaluacion && probabilidadExito) p.set("probabilidad_exito", probabilidadExito);
    if (veEvaluacion && valorEstrategicoId) p.set("valor_estrategico_id", valorEstrategicoId);
    if (veEvaluacion && soloEstrategicos === "1") p.set("solo_estrategicos", "1");
    return p.toString();
  }, [
    busqueda,
    estado,
    prioridad,
    materiaId,
    submateriaId,
    fueroId,
    veEvaluacion,
    estadoProcesalId,
    nivelRiesgo,
    probabilidadExito,
    valorEstrategicoId,
    soloEstrategicos,
  ]);

  // Chips de filtros activos — puramente derivado de las mismas variables
  // que ya arman `parametros`, sin agregar ningún estado nuevo. La
  // resolución de nombre para Materia/Submateria/Fuero/Estado Procesal/
  // Valor Estratégico reutiliza las listas ya cargadas para llenar esos Select.
  const CHIP_NEUTRO = "bg-secundario/10 text-secundario";

  const chips = useMemo(() => {
    const items: { clave: string; etiqueta: string; clase: string; onQuitar: () => void }[] = [];
    if (estado) items.push({ clave: "estado", etiqueta: `Estado: ${estado}`, clase: CHIP_NEUTRO, onQuitar: () => setEstado("") });
    if (prioridad) items.push({ clave: "prioridad", etiqueta: `Prioridad: ${prioridad}`, clase: CHIP_NEUTRO, onQuitar: () => setPrioridad("") });
    if (materiaId) {
      const nombre = materias.find((m) => m.id === materiaId)?.nombre ?? materiaId;
      items.push({
        clave: "materia",
        etiqueta: `Materia: ${nombre}`,
        clase: CHIP_NEUTRO,
        onQuitar: () => actualizarFiltrosUrl({ materia_id: "", submateria_id: "" }),
      });
    }
    if (submateriaId) {
      const nombre = submaterias.find((s) => s.id === submateriaId)?.nombre ?? submateriaId;
      items.push({ clave: "submateria", etiqueta: `Submateria: ${nombre}`, clase: CHIP_NEUTRO, onQuitar: () => setSubmateriaId("") });
    }
    if (fueroId) {
      const nombre = fueros.find((f) => f.id === fueroId)?.nombre ?? fueroId;
      items.push({ clave: "fuero", etiqueta: `Fuero: ${nombre}`, clase: CHIP_NEUTRO, onQuitar: () => setFueroId("") });
    }
    if (veEvaluacion && estadoProcesalId) {
      const item = estadosProcesales.find((e) => e.id === estadoProcesalId);
      items.push({
        clave: "estadoProcesal",
        etiqueta: `Estado procesal: ${item?.nombre ?? estadoProcesalId}`,
        clase: resolverColorSemantico(item?.color).chip,
        onQuitar: () => setEstadoProcesalId(""),
      });
    }
    if (veEvaluacion && nivelRiesgo) {
      items.push({
        clave: "riesgo",
        etiqueta: `Riesgo: ${nivelRiesgo}`,
        clase: resolverColorSemantico(COLOR_POR_RIESGO[nivelRiesgo]).chip,
        onQuitar: () => setNivelRiesgo(""),
      });
    }
    if (veEvaluacion && probabilidadExito) {
      items.push({
        clave: "probabilidad",
        etiqueta: `Probabilidad: ${probabilidadExito}`,
        clase: resolverColorSemantico(COLOR_POR_PROBABILIDAD[probabilidadExito]).chip,
        onQuitar: () => setProbabilidadExito(""),
      });
    }
    if (veEvaluacion && valorEstrategicoId) {
      const nombre = valoresEstrategicos.find((v) => v.id === valorEstrategicoId)?.nombre ?? valorEstrategicoId;
      items.push({ clave: "valorEstrategico", etiqueta: `Valor estratégico: ${nombre}`, clase: CHIP_NEUTRO, onQuitar: () => setValorEstrategicoId("") });
    }
    if (veEvaluacion && soloEstrategicos === "1") {
      items.push({ clave: "estrategicos", etiqueta: "Expedientes Estratégicos", clase: CHIP_NEUTRO, onQuitar: () => setSoloEstrategicos("") });
    }
    return items;
  }, [
    estado,
    prioridad,
    materiaId,
    submateriaId,
    fueroId,
    materias,
    submaterias,
    fueros,
    veEvaluacion,
    estadoProcesalId,
    nivelRiesgo,
    probabilidadExito,
    valorEstrategicoId,
    soloEstrategicos,
    estadosProcesales,
    valoresEstrategicos,
  ]);

  function limpiarFiltros() {
    actualizarFiltrosUrl(Object.fromEntries(CLAVES_FILTROS_URL.map((clave) => [clave, ""])));
  }

  function cambiarMateria(nuevaMateriaId: string) {
    // Al cambiar la Materia se limpia la Submateria — mismo criterio que
    // FormularioExpediente.tsx: no debe quedar seleccionada una Submateria
    // que ya no es compatible con la Materia elegida.
    actualizarFiltrosUrl({ materia_id: nuevaMateriaId, submateria_id: "" });
  }

  useEffect(() => {
    setCargando(true);
    api
      .get<{ items: ExpedienteConCliente[] }>(`/expedientes?${parametros}`)
      .then((d) => setExpedientes(d.items))
      .catch(() => mostrar("No se pudieron cargar los expedientes.", "error"))
      .finally(() => setCargando(false));
  }, [parametros, mostrar]);

  useEffect(() => {
    api
      .get<{ items: Cliente[] }>("/clientes")
      .then((d) => setClientes(d.items))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    api
      .get<{ items: Materia[] }>("/catalogos/materias")
      .then((d) => setMaterias(d.items))
      .catch(() => undefined);
    api
      .get<{ items: Fuero[] }>("/catalogos/fueros")
      .then((d) => setFueros(d.items))
      .catch(() => undefined);
  }, []);

  // Submaterias dependen de la Materia elegida en el filtro — mismo patrón
  // de precarga que FormularioExpediente.tsx (incluye la restauración desde
  // la URL: si `materia_id` ya viene en el link, esto carga sus submaterias
  // para que el chip/select de Submateria puedan resolver el nombre).
  useEffect(() => {
    if (!materiaId) {
      setSubmaterias([]);
      return;
    }
    api
      .get<{ items: Submateria[] }>(`/catalogos/materias/${materiaId}/submaterias`)
      .then((d) => setSubmaterias(d.items))
      .catch(() => undefined);
  }, [materiaId]);

  useEffect(() => {
    if (!veEvaluacion) return;
    api
      .get<{ items: EstadoProcesal[] }>("/catalogos-estudio/estados-procesales")
      .then((d) => setEstadosProcesales(d.items.filter((e) => e.activo)))
      .catch(() => undefined);
    api
      .get<{ items: ValorEstrategico[] }>("/catalogos-estudio/valores-estrategicos")
      .then((d) => setValoresEstrategicos(d.items.filter((v) => v.activo)))
      .catch(() => undefined);
  }, [veEvaluacion]);

  async function crearExpediente(valores: Record<string, unknown>) {
    setGuardando(true);
    try {
      const datos = await api.post<{ expediente: Expediente }>(
        "/expedientes",
        valores,
      );
      mostrar("Expediente creado correctamente.", "exito");
      setModalAbierto(false);
      navegar(`/expedientes/${datos.expediente.id}`);
    } catch (error) {
      mostrar(
        error instanceof ErrorApi
          ? error.message
          : "No se pudo crear el expediente.",
        "error",
      );
    } finally {
      setGuardando(false);
    }
  }

  const columnas: ColumnaTabla<ExpedienteConCliente>[] = [
    {
      encabezado: "Número",
      render: (e) => (
        <span className="font-medium text-texto">
          {e.numero_expediente}
        </span>
      ),
    },
    { encabezado: "Cliente", render: (e) => e.cliente_nombre },
    {
      encabezado: "Materia",
      render: (e) => (
        <div>
          <span className="inline-flex items-center gap-1.5">
            <span className={`h-2 w-2 flex-shrink-0 rounded-full ${resolverColorSemantico(e.materia_color).punto}`} />
            {e.materia_nombre}
          </span>
          {e.submateria_nombre && (
            <span className="block text-xs text-texto-secundario">
              {e.submateria_nombre}
            </span>
          )}
        </div>
      ),
    },
    {
      encabezado: "Organismo",
      render: (e) => (
        <div>
          <span>{e.organismo_nombre ?? "—"}</span>
          {e.secretaria_nombre && (
            <span className="block text-xs text-texto-secundario">
              {e.secretaria_nombre}
            </span>
          )}
        </div>
      ),
    },
    { encabezado: "Estado", render: (e) => <Insignia texto={e.estado} /> },
    {
      encabezado: "Prioridad",
      render: (e) => <Insignia texto={e.prioridad} tipo="prioridad" />,
    },
    { encabezado: "Responsable", render: (e) => e.abogado_responsable_nombre ?? "Sin responsable" },
    { encabezado: "Apertura", render: (e) => e.fecha_apertura },
    { encabezado: "Avance", render: (e) => <BarraAvance pct={e.avance_manual_pct} /> },
  ];

  return (
    <div>
      <EncabezadoPagina
        titulo="Expedientes"
        descripcion="Gestión de expedientes del estudio"
        acciones={
          <>
            <div className="flex overflow-hidden rounded-lg border border-borde">
              <button
                onClick={() => setVista("tabla")}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  vista === "tabla" ? "bg-principal text-superficie" : "bg-superficie text-texto-secundario hover:text-texto"
                }`}
              >
                Tabla
              </button>
              <button
                onClick={() => setVista("tarjetas")}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  vista === "tarjetas" ? "bg-principal text-superficie" : "bg-superficie text-texto-secundario hover:text-texto"
                }`}
              >
                Tarjetas
              </button>
            </div>
            <Boton onClick={() => setModalAbierto(true)}>
              + Nuevo Expediente
            </Boton>
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <CampoBusqueda
          valor={busqueda}
          onCambio={setBusqueda}
          placeholder="Buscar por número o cliente..."
        />
        <button
          type="button"
          onClick={() => setFiltrosAbiertos(true)}
          className="flex items-center gap-2 rounded-lg border border-borde bg-superficie px-3 py-2 text-sm font-medium text-texto transition-colors hover:bg-fondo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secundario focus-visible:ring-offset-2"
        >
          <Icono nombre="filtro" className="h-4 w-4" />
          Filtros
          {chips.length > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-secundario px-1.5 text-xs font-semibold text-superficie">
              {chips.length}
            </span>
          )}
        </button>
      </div>

      {chips.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {chips.map((chip) => (
            <span
              key={chip.clave}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${chip.clase}`}
            >
              {chip.etiqueta}
              <button
                type="button"
                onClick={chip.onQuitar}
                aria-label={`Quitar filtro ${chip.etiqueta}`}
                className="rounded-full opacity-70 hover:opacity-100"
              >
                <Icono nombre="cerrar" className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={limpiarFiltros}
            className="text-xs font-medium text-texto-secundario underline-offset-2 hover:text-texto hover:underline"
          >
            Limpiar filtros
          </button>
        </div>
      )}

      <PanelLateral titulo="Filtros" abierto={filtrosAbiertos} onCerrar={() => setFiltrosAbiertos(false)}>
        <div className="space-y-4">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-texto">Estado</span>
            <Select value={estado} onChange={(e) => setEstado(e.target.value)}>
              <option value="">Todos los estados</option>
              {ESTADOS.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </Select>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-texto">Prioridad</span>
            <Select value={prioridad} onChange={(e) => setPrioridad(e.target.value)}>
              <option value="">Toda prioridad</option>
              {PRIORIDADES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-texto">Materia</span>
            <Select value={materiaId} onChange={(e) => cambiarMateria(e.target.value)}>
              <option value="">Toda materia</option>
              {materias.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nombre}
                </option>
              ))}
            </Select>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-texto">Submateria</span>
            <Select value={submateriaId} disabled={!materiaId} onChange={(e) => setSubmateriaId(e.target.value)}>
              <option value="">{materiaId ? "Toda submateria" : "Elegí una materia primero"}</option>
              {submaterias.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </Select>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-texto">Fuero</span>
            <Select value={fueroId} onChange={(e) => setFueroId(e.target.value)}>
              <option value="">Todo fuero</option>
              {fueros.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.nombre}
                </option>
              ))}
            </Select>
          </label>
          {veEvaluacion && (
            <>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-texto">Estado procesal</span>
                <Select value={estadoProcesalId} onChange={(e) => setEstadoProcesalId(e.target.value)}>
                  <option value="">Todo estado procesal</option>
                  {estadosProcesales.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.nombre}
                    </option>
                  ))}
                </Select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-texto">Riesgo</span>
                <Select value={nivelRiesgo} onChange={(e) => setNivelRiesgo(e.target.value)}>
                  <option value="">Todo riesgo</option>
                  {NIVELES_RIESGO.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-texto">Probabilidad</span>
                <Select value={probabilidadExito} onChange={(e) => setProbabilidadExito(e.target.value)}>
                  <option value="">Toda probabilidad</option>
                  {PROBABILIDADES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </Select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-texto">Valor estratégico</span>
                <Select value={valorEstrategicoId} onChange={(e) => setValorEstrategicoId(e.target.value)}>
                  <option value="">Todo valor estratégico</option>
                  {valoresEstrategicos.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.nombre}
                    </option>
                  ))}
                </Select>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={soloEstrategicos === "1"}
                  onChange={(e) => setSoloEstrategicos(e.target.checked ? "1" : "")}
                  className="h-4 w-4 rounded border-borde"
                />
                <span className="text-sm font-medium text-texto">Expedientes Estratégicos</span>
              </label>
            </>
          )}
          {chips.length > 0 && (
            <Boton variante="secundario" onClick={limpiarFiltros} className="w-full">
              Limpiar filtros
            </Boton>
          )}
        </div>
      </PanelLateral>

      {vista === "tabla" ? (
        <Tabla
          columnas={columnas}
          filas={expedientes}
          claveFila={(e) => e.id}
          cargando={cargando}
          onFilaClick={(e) => irAFicha(e.id)}
          tituloVacio={
            sinAreasAsignadas ? "No tenés Unidades de Negocio asignadas" : "No hay expedientes"
          }
          descripcionVacio={
            sinAreasAsignadas
              ? "Solicitá al administrador que configure tu acceso."
              : "Creá el primer expediente del estudio."
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {expedientes.map((e) => {
            const dias = diasSinMovimiento(e);
            return (
              <Tarjeta
                key={e.id}
                className="cursor-pointer transition-shadow hover:shadow-md"
                tamano="compacta"
              >
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div onClick={() => irAFicha(e.id)}>
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <span className="font-medium text-texto">{e.numero_expediente}</span>
                    <span
                      title={`${dias} días sin movimiento`}
                      className={`mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full ${colorSemaforo(dias)}`}
                    />
                  </div>
                  <p className="truncate text-sm text-texto-secundario">{e.cliente_nombre}</p>
                  <p className="mt-1 truncate text-xs text-texto-secundario">
                    {e.materia_nombre}
                    {e.submateria_nombre ? ` · ${e.submateria_nombre}` : ""}
                  </p>
                  <p className="mt-1 truncate text-xs text-texto-secundario">
                    Responsable: {e.abogado_responsable_nombre ?? "Sin responsable"}
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <Insignia texto={e.estado} />
                    <Insignia texto={e.prioridad} tipo="prioridad" />
                  </div>
                  <div className="mt-3">
                    <BarraAvance pct={e.avance_manual_pct} />
                  </div>
                </div>
              </Tarjeta>
            );
          })}
          {expedientes.length === 0 && !cargando && (
            <p className="col-span-full text-center text-sm text-texto-secundario">
              {sinAreasAsignadas
                ? "No tenés Unidades de Negocio asignadas. Solicitá al administrador que configure tu acceso."
                : "No hay expedientes."}
            </p>
          )}
        </div>
      )}

      <FormularioExpediente
        abierto={modalAbierto}
        clientes={clientes}
        guardando={guardando}
        onCerrar={() => setModalAbierto(false)}
        onGuardar={crearExpediente}
      />
    </div>
  );
}
